import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/react-hooks';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import Button from 'WeployDesignLanguage/buttons/Button';
import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import RadioButtons from 'LegacyWeployDesignLanguage/inputs/RadioButtons';
import Snackbar from 'LegacyWeployDesignLanguage/Snackbar';
import Table from '../../../components/weployer/common/table';
import {
  GET_USERS,
  CHANGE_WEPLOYER_ROLE_MUTATION,
  CREATE_INVITATION_MUTATION,
  DELETE_ORGANISATION_USER_MUTATION,
} from './queries';
import EditRoleDialog from '../../../components/weployer/users/EditRoleDialog';
import ConfirmDeleteWeployerDialog from '../../../components/weployer/users/ConfirmDeleteWeployerDialog';
import DeleteUserFailureDialog from '../../../components/weployer/users/DeleteUserFailureDialog';
import UserRow from '../../../components/weployer/users/UserRow';
import InvitationRow from '../../../components/weployer/users/InvitationRow';
import {
  NEW_INVITATION_PATH,
  getExternalPath,
} from '../pathHelpers';
import {
  StyledButton,
  StyledContactAdminMessage,
  StyledDescription,
  StyledInfoOutlinedIcon,
  StyledInlineButton,
  StyledRadioButtonsWrapper,
  StyledRadioDescription,
  StyledRadioLabel,
  StyledUsersContainer,
} from '../../../components/weployer/users/style';
import { mapUsersAndInvitations } from './mappers';
import { PolicyContext, ASSIGN_ORGANISATION_WEPLOYER_ROLE } from '../PolicyProvider';

const message = 'Getting control of your organisation is hard, but we make it easy. Invite colleagues and assign roles so Admins get full control, and Managers can just manage.';

const renderRadioLabel = (label, description) => (
  <>
    <StyledRadioLabel>{label}</StyledRadioLabel>
    <StyledRadioDescription>{description}</StyledRadioDescription>
  </>
);

const editedWeployerEmptyState = {
  id: undefined,
  initialRole: undefined,
  role: undefined,
};

const deletedWeployerEmptyState = {
  id: undefined,
  name: undefined,
  hasActiveJobs: undefined,
};

const useSnackbar = () => {
  const queueRef = useRef([]);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState(undefined);

  const processQueue = () => {
    if (queueRef.current.length > 0) {
      setSnackbarMessage(queueRef.current.shift());
      setSnackbarOpen(true);
    }
  };

  const enqueueSnackbar = (msg) => {
    queueRef.current.push({
      msg,
      key: new Date().getTime(),
    });
    if (snackbarOpen) {
      setSnackbarOpen(false);
    } else {
      processQueue();
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSnackbarExit = () => {
    processQueue();
  };

  return [
    snackbarOpen,
    snackbarMessage,
    enqueueSnackbar,
    handleSnackbarClose,
    handleSnackbarExit,
  ];
};

const Users = ({ navigateExternal }) => {
  const { loading, data, refetch } = useQuery(GET_USERS);
  const [snackbarOpen, snackbarMessage, enqueueSnackbar, handleSnackbarClose, handleSnackbarExit] = useSnackbar();
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [deleteWeployerModalOpen, setDeleteWeployerModalOpen] = useState(false);
  const [deleteWeployerFailureModalOpen, setDeleteWeployerFailureModalOpen] = useState(false);
  const [editedWeployer, setEditedWeployer] = useState(editedWeployerEmptyState);
  const [deletedWeployer, setDeletedWeployer] = useState(deletedWeployerEmptyState);
  const [changeRole] = useMutation(CHANGE_WEPLOYER_ROLE_MUTATION);
  const [resendInvitation] = useMutation(CREATE_INVITATION_MUTATION);
  const [deleteWeployer] = useMutation(DELETE_ORGANISATION_USER_MUTATION);
  const { actions } = useContext(PolicyContext);

  const weployers = data && data.weployer.currentOrganisation.weployers;
  const invitations = data && data.weployer.currentOrganisation.visibleInvitations;
  const usersAndInvitations = mapUsersAndInvitations(weployers, invitations);

  const handleLinkClick = path => (event) => {
    event.preventDefault();
    navigateExternal(getExternalPath({ pathName: path }));
  };

  // Edit role
  const handleEditRoleModalClose = () => {
    setEditedWeployer(editedWeployerEmptyState);
    setEditRoleModalOpen(false);
  };

  const handleEditRoleButtonClick = (focusedWeployer) => {
    setEditRoleModalOpen(true);
    setEditedWeployer({
      ...editedWeployer,
      ...focusedWeployer,
    });
  };

  const handleChooseEditedRole = (event) => {
    const newRole = event.target.value;
    setEditedWeployer({
      ...editedWeployer,
      role: newRole,
    });
  };

  const handleEditRole = async (focusedWeployer) => {
    const variables = {
      organisationId: data.weployer.currentOrganisation.id,
      weployerId: focusedWeployer.id,
      role: focusedWeployer.role,
    };

    handleEditRoleModalClose();
    enqueueSnackbar('Saving new role...');

    await changeRole({ variables });

    enqueueSnackbar('Role changed successfully!');
    refetch();
  };

  // Resend invitation
  const handleResendButtonClick = async (receiverRole, recipientEmail) => {
    enqueueSnackbar('Resending ...');

    const variables = {
      receiverRole,
      recipientEmail,
    };

    await resendInvitation({ variables });

    refetch();
  };

  // Remove user
  const handleDeleteWeployerFailureModalClose = () => {
    setDeletedWeployer(deletedWeployerEmptyState);
    setDeleteWeployerFailureModalOpen(false);
  };

  const handleDeleteWeployerModalClose = () => {
    setDeletedWeployer(deletedWeployerEmptyState);
    setDeleteWeployerModalOpen(false);
  };

  const handleRemoveUserButtonClick = (weployer) => {
    setDeletedWeployer({ id: weployer.id, name: weployer.name, hasActiveJobs: weployer.hasActiveJobs });

    if (weployer.hasActiveJobs) {
      setDeleteWeployerFailureModalOpen(true);
    } else {
      setDeleteWeployerModalOpen(true);
    }
  };

  const handleConfirmDeleteWeployer = async (weployer) => {
    handleDeleteWeployerModalClose();
    enqueueSnackbar('Removing user...');

    await deleteWeployer({ variables: { weployerId: weployer.id } });

    refetch();
    enqueueSnackbar('User has been removed.');
  };


  const renderUsersList = items => (
    <Table>
      <Table.Body>
        {
          items.map(item => (item.type === 'user' ?
            <UserRow key={item.id} user={item} handleEditRoleButtonClick={handleEditRoleButtonClick} handleRemoveUserButtonClick={handleRemoveUserButtonClick} showEditRoleMenuItem={actions.includes(ASSIGN_ORGANISATION_WEPLOYER_ROLE)} /> :
            <InvitationRow key={item.id} invitation={item} showResendCta={item.canResend} onResendClick={() => handleResendButtonClick(item.receiverRole, item.key)} />
          ))
        }
      </Table.Body>
    </Table>
  );

  return (
    <StyledUsersContainer>
      <h5>Users</h5>
      <StyledDescription>{message}&nbsp;<StyledInlineButton onClick={() => window.open('https://www.weployapp.com/weployer/help')}>Learn more</StyledInlineButton>
      </StyledDescription>
      <StyledButton
        startIcon={<PersonAddIcon />}
        onClick={handleLinkClick(NEW_INVITATION_PATH)}
      >
        Invite a Colleague
      </StyledButton>
      {
        !actions.includes(ASSIGN_ORGANISATION_WEPLOYER_ROLE) &&
          <StyledContactAdminMessage>
            <StyledInfoOutlinedIcon />
            <span>You can only view users. Please contact the admin if you wish to perform any actions</span>
          </StyledContactAdminMessage>
      }
      <Loader loading={loading} text="Loading..." >
        {(!loading && usersAndInvitations) && renderUsersList(usersAndInvitations) }
      </Loader>
      <>
        <EditRoleDialog
          open={editRoleModalOpen}
          onClose={handleEditRoleModalClose}
          content={
            <StyledRadioButtonsWrapper>
              <RadioButtons
                name="payCategory"
                value={editedWeployer.role || ''}
                labelPlacement="start"
                onChange={handleChooseEditedRole}
                radios={[
                  { label: renderRadioLabel('Admin', 'Can post and manage their own jobs plus see all jobs in the organisation, edit user roles and update billing information'), value: 'ADMIN' },
                  { label: renderRadioLabel('Manager', 'Can post and manage their own jobs'), value: 'MANAGER' },
                ]}
              />
            </StyledRadioButtonsWrapper>
          }
          confirmAction={
            <Button
              disabled={editedWeployer.role === editedWeployer.initialRole}
              appearance={Button.TEXT}
              onClick={() => handleEditRole(editedWeployer)}
            >
              Save
            </Button>
          }
        />
        <ConfirmDeleteWeployerDialog
          open={deleteWeployerModalOpen}
          onClose={handleDeleteWeployerModalClose}
          content={`Are you sure you want to remove ${deletedWeployer.name} from your organisation?`}
          confirmAction={
            <Button
              appearance={Button.TEXT}
              onClick={() => handleConfirmDeleteWeployer(deletedWeployer)}
            >
              Confirm
            </Button>
          }
        />
        <DeleteUserFailureDialog
          open={deleteWeployerFailureModalOpen}
          onClose={handleDeleteWeployerFailureModalClose}
          title={`Failed to remove ${deletedWeployer.name}`}
          content={`${deletedWeployer.name} still has current jobs. Please transfer ownership of all current jobs and try again`}
        />
        <Snackbar
          autoHideDuration={2000}
          key={snackbarMessage ? snackbarMessage.key : undefined}
          visible={snackbarOpen}
          onClose={handleSnackbarClose}
          onExited={handleSnackbarExit}
          message={snackbarMessage ? snackbarMessage.msg : ''}
        />
      </>
    </StyledUsersContainer>
  );
};

Users.propTypes = {
  navigateExternal: PropTypes.func,
};

Users.defaultProps = {
  navigateExternal: () => {},
};

export default Users;
